import React from 'react'

import Checkbox from '@material-ui/core/Checkbox'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'

import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'

// @src imports
import { LEGACY_SITE_URL } from 'src/app/api'

import { Button as ChromeButton, Dialog } from 'src/chrome'
import { AddressForm } from 'src/contacts/components'
import { compact } from 'src/helpers'

import styles from './affiliateForm.module.scss'
import { useAccount, useState } from 'src/hooks'
import { Spacer, Text } from '@sendoutcards/quantum-design-ui'

const AffiliateAgreement = () => (
  <a
    href={`https://www.sendoutcards.com/resources/documents/391/Affiliate_Agreement_Documents_June_11_2020.pdf`}
  >
    Affiliate Agreement
  </a>
)

const Compensation = () => (
  <a
    href={`${LEGACY_SITE_URL}/resources/documents/234/COMPENSATION_PLAN_DETAILS_2019.pdf`}
  >
    Compensation Plan
  </a>
)

type AffiliateFormProps = {
  purchase: (affiliateInfo: AffiliateFormInfo) => void
  close: () => void
  isTrialAffiliate?: true
  hasPhoneNumber?: boolean
}

export type AffiliateFormInfo = {
  govId1: string
  govId2: string
  user: UserInfo
  phoneNumber: string
  accepted: boolean
  purchased: boolean
  stripeToken?: stripe.Token
}

type UserInfo = {
  firstName: string
  lastName: string
  company: string
  address1: string
  address2: string
  city: string
  state: string
  country: string
  postalCode: string
}

const AffiliateForm: React.FC<AffiliateFormProps> = props => {
  const { close, purchase, isTrialAffiliate, hasPhoneNumber } = props

  const account = useAccount()
  const address = account.shippingAddress

  const [govId1, setGovId1] = useState('')
  const [govId2, setGovId2] = useState('')

  const [user, setUser] = useState<UserInfo>({
    firstName: account.firstName,
    lastName: account.lastName,
    company: address?.company ?? '',
    address1: address?.address1 ?? '',
    address2: address?.address2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    country: address?.country ?? 'United States',
    postalCode: address?.postalCode ?? '',
  })

  const [phoneNumber, setPhoneNumber] = useState(account.phoneNumber)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [isAccepted, setIsAccepted] = useState(false)

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => setPhoneNumber(event.currentTarget.value)

  const isPhoneInvalid = phoneNumber.search(/[^0-9 ()+-]+/) !== -1
  const isFormInvalid = isPhoneInvalid

  const handleGovId1Change = (event: React.ChangeEvent<HTMLInputElement>) =>
    setGovId1(event.currentTarget.value)

  const handleGovId2Change = (event: React.ChangeEvent<HTMLInputElement>) =>
    setGovId2(event.currentTarget.value)

  const handleChildInput = (event: React.ChangeEvent<HTMLInputElement>) =>
    setUser({
      ...user,
      [event.target.name]: event.target.value,
    })

  const handleCountryChange = (value: string) =>
    setUser({
      ...user,
      country: value,
    })

  const handleStateChange = (value: string) =>
    setUser({
      ...user,
      state: value,
    })

  const handleCheckbox = () => setIsAccepted(!isAccepted)

  const validateForm = () => {
    const errors = compact(
      user.firstName === '' && 'First Name',
      user.lastName === '' && 'Last Name',
      user.address1 === '' && 'Address 1',
      user.city === '' && 'City',
      user.state === '' && 'State',
      user.country === '' && 'Country',
      user.postalCode === '' && 'Postal Code',
      phoneNumber === '' && 'Phone Number',
      govId1 === '' && 'Government ID #1',
      user.country !== 'United States' && govId2 === '' && 'Government ID #2',
      !isAccepted && 'You must agree to the terms of service.',
    )

    if (errors.length > 0) {
      setFormErrors(errors)
    } else {
      setFormErrors([])
      purchase({
        govId1,
        govId2,
        user,
        phoneNumber,
        accepted: isAccepted,
        purchased: false,
      })
    }
  }

  return (
    <Dialog open={true} scroll={'paper'} onClose={close}>
      <DialogTitle disableTypography={true}>
        {<Text type="caption">Become an Affiliate</Text>}
      </DialogTitle>
      <DialogContent className={styles.container}>
        {formErrors.length > 0 && (
          <div className={styles.errors}>
            <Text type="largeBody">Required Fields</Text>
            {formErrors.map((error, index) => (
              <span key={index}>
                <Text type="body">{error}</Text>
              </span>
            ))}
          </div>
        )}
        <div className={styles.userForm}>
          <AddressForm
            contact={user}
            onChange={handleChildInput}
            shouldHideActions={true}
            onCountryChange={handleCountryChange}
            onStateChange={handleStateChange}
            shouldShowDates={false}
            isTitleVisible={false}
          />
          {!hasPhoneNumber && (
            <TextField
              label={'Phone Number'}
              InputLabelProps={{
                classes: { root: styles.inputLabel },
              }}
              value={phoneNumber}
              name={'phoneNumber'}
              onChange={handlePhoneNumberChange}
              error={isPhoneInvalid}
              helperText={
                isPhoneInvalid
                  ? 'Phone numbers may be only numbers, spaces, and special characters: + ( ) -'
                  : null
              }
            />
          )}

          <Spacer space="x2" />
          <Text type="body">Affiliate Verification</Text>
          <div className={styles.identification}>
            <TextField
              label={'Government ID #1 (EIN or SSN)'}
              InputLabelProps={{
                classes: { root: styles.inputLabel },
              }}
              value={govId1}
              name={'govId1'}
              onChange={handleGovId1Change}
            />
            {user.country !== 'United States' && (
              <TextField
                label={'Government ID #2 (Non-USA)'}
                InputLabelProps={{
                  classes: { root: styles.inputLabel },
                }}
                value={govId2}
                name={'govId2'}
                onChange={handleGovId2Change}
              />
            )}
          </div>
          <div className={styles.accept}>
            <Checkbox style={{ width: 35 }} onChange={handleCheckbox} />
            <Text type="caption">
              I agree to the <AffiliateAgreement /> and the <Compensation /> and
              authorize SendOutCards to bill me $79 annually for the Affiliate
              fee.
            </Text>
          </div>
          <div className={styles.submit}>
            <ChromeButton
              id={'review_affiliate_btn'}
              title={!isTrialAffiliate ? 'Review and Upgrade' : 'Submit'}
              onClick={validateForm}
              style={{ marginTop: 50, width: 200 }}
              disabled={isFormInvalid}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button color={'primary'} onClick={close}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AffiliateForm
